/**
 * EditableMarkdownOverlay - Fullscreen markdown viewer with edit mode
 *
 * Composes FullscreenOverlayBase with:
 * - Read mode: renders markdown via the Markdown component (same as DocumentFormattedMarkdownOverlay)
 * - Edit mode: renders a Tiptap rich-text editor with formatting toolbar
 *
 * State model:
 *   savedContent  — last known on-disk content (baseline for dirty check)
 *   draftContent  — current in-memory content (may differ from saved)
 *   isDirty       — draftContent !== savedContent
 *
 * Only used for real files on disk (has a filePath). Tool outputs and
 * release notes continue to use DocumentFormattedMarkdownOverlay.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { ListTodo, RefreshCw, PenLine, Eye, Save, Check, Loader2 } from 'lucide-react'
import { FullscreenOverlayBase, Markdown, TiptapMarkdownEditor, cn } from '@craft-agent/ui'
import { UnsavedChangesDialog } from './UnsavedChangesDialog'
import { toast } from 'sonner'

interface EditableMarkdownOverlayProps {
  content: string
  isOpen: boolean
  onClose: () => void
  filePath: string
  variant?: 'response' | 'plan'
  onOpenUrl?: (url: string) => void
  onOpenFile?: (path: string) => void
  onRefresh?: () => void
}

export function EditableMarkdownOverlay({
  content,
  isOpen,
  onClose,
  filePath,
  variant = 'response',
  onOpenUrl,
  onOpenFile,
  onRefresh,
}: EditableMarkdownOverlayProps) {
  const [mode, setMode] = useState<'read' | 'edit'>('read')
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)

  // Core state model: savedContent (on-disk baseline) + draftContent (in-memory edits)
  const [savedContent, setSavedContent] = useState(content)
  const [draftContent, setDraftContent] = useState(content)
  const isDirty = draftContent !== savedContent

  const isSavingRef = useRef(false)
  const saveSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevContentRef = useRef(content)

  // Sync savedContent when the content prop actually changes (e.g., parent re-read after refresh).
  // Only fires on real prop changes — NOT when isDirty toggles (which would revert saves).
  // Skipped when dirty to avoid discarding unsaved edits.
  useEffect(() => {
    if (content !== prevContentRef.current) {
      prevContentRef.current = content
      if (!isDirty) {
        setSavedContent(content)
        setDraftContent(content)
      }
    }
  }, [content, isDirty])

  // Cleanup saveSuccess timeout on unmount
  useEffect(() => {
    return () => {
      if (saveSuccessTimeoutRef.current) clearTimeout(saveSuccessTimeoutRef.current)
    }
  }, [])

  // Editor onChange — keeps draftContent in sync with editor state
  const handleEditorChange = useCallback((markdown: string) => {
    setDraftContent(markdown)
  }, [])

  const handleSave = useCallback(async () => {
    if (isSavingRef.current) return
    isSavingRef.current = true
    setIsSaving(true)

    // draftContent is kept in sync by the editor's onUpdate callback
    const markdown = draftContent

    try {
      await window.electronAPI.writeFile(filePath, markdown)
      setSavedContent(markdown)
      setDraftContent(markdown)

      if (saveSuccessTimeoutRef.current) clearTimeout(saveSuccessTimeoutRef.current)
      setSaveSuccess(true)
      saveSuccessTimeoutRef.current = setTimeout(() => setSaveSuccess(false), 1500)
    } catch (error) {
      console.error('Failed to save file:', error)
      toast.error('Failed to save file')
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [filePath, draftContent])

  // Close request — prompt if dirty regardless of mode
  const handleCloseRequest = useCallback(() => {
    if (isDirty) {
      setShowUnsavedDialog(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleSwitchToEdit = useCallback(() => {
    setMode('edit')
  }, [])

  const handleSwitchToRead = useCallback(() => {
    setMode('read')
  }, [])

  const handleDialogDiscard = useCallback(() => {
    setShowUnsavedDialog(false)
    // Reset draft to saved content (discard changes)
    setDraftContent(savedContent)
    setMode('read')
    onClose()
  }, [onClose, savedContent])

  const handleDialogSave = useCallback(async () => {
    setShowUnsavedDialog(false)
    if (isSavingRef.current) return
    isSavingRef.current = true
    setIsSaving(true)

    const markdown = draftContent

    try {
      await window.electronAPI.writeFile(filePath, markdown)
      setSavedContent(markdown)
      setDraftContent(markdown)
      setMode('read')
      onClose()
    } catch (error) {
      console.error('Failed to save file:', error)
      toast.error('Failed to save file')
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [filePath, onClose, draftContent])

  const handleDialogCancel = useCallback(() => {
    setShowUnsavedDialog(false)
  }, [])

  // Cmd+S / Ctrl+S keyboard shortcut in edit mode
  useEffect(() => {
    if (mode !== 'edit') return
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [mode, handleSave])

  // Build header actions
  const headerActions = (
    <div className="flex items-center gap-1.5">
      {/* Save button — visible when dirty in any mode */}
      {isDirty && (
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={cn(
            'p-1.5 rounded-[6px] bg-background shadow-minimal cursor-pointer',
            'opacity-70 hover:opacity-100 transition-opacity',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:opacity-40 disabled:cursor-not-allowed'
          )}
          title="Save (⌘S)"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveSuccess ? (
            <Check className="w-4 h-4 text-success" />
          ) : (
            <Save className="w-4 h-4" />
          )}
        </button>
      )}

      {/* Edit / Read toggle */}
      <button
        onClick={mode === 'read' ? handleSwitchToEdit : handleSwitchToRead}
        className={cn(
          'p-1.5 rounded-[6px] bg-background shadow-minimal cursor-pointer',
          'opacity-70 hover:opacity-100 transition-opacity',
          'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'flex items-center gap-1.5'
        )}
        title={mode === 'read' ? 'Edit' : 'Read'}
      >
        {mode === 'read' ? (
          <>
            <PenLine className="w-4 h-4" />
            <span className="text-xs font-medium">Edit</span>
          </>
        ) : (
          <>
            <Eye className="w-4 h-4" />
            <span className="text-xs font-medium">Read</span>
          </>
        )}
      </button>

      {/* Refresh button — only in read mode when not dirty */}
      {mode === 'read' && !isDirty && onRefresh && (
        <button
          onClick={onRefresh}
          className={cn(
            'p-1.5 rounded-[6px] bg-background shadow-minimal cursor-pointer',
            'opacity-70 hover:opacity-100 transition-opacity',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'
          )}
          title="Refresh from disk"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      )}
    </div>
  )

  const planHeader = variant === 'plan' && (
    <div className="px-4 py-2 border-b border-border/30 flex items-center gap-2 bg-success/5 rounded-t-[16px]">
      <ListTodo className="w-3 h-3 text-success" />
      <span className="text-[13px] font-medium text-success">Plan</span>
    </div>
  )

  return (
    <>
      <FullscreenOverlayBase
        isOpen={isOpen}
        onClose={handleCloseRequest}
        filePath={filePath}
        copyContent={draftContent}
        headerActions={headerActions}
      >
        <div className="min-h-full flex flex-col justify-center px-6 pt-2.5 pb-16">
          <div className="bg-background rounded-[16px] shadow-strong w-full max-w-[1600px] h-fit mx-auto my-auto">
            {planHeader}

            {mode === 'read' ? (
              <div className="px-10 pt-8 pb-8">
                <div className="text-sm">
                  <Markdown
                    mode="minimal"
                    onUrlClick={onOpenUrl}
                    onFileClick={onOpenFile}
                    hideFirstMermaidExpand={false}
                  >
                    {draftContent}
                  </Markdown>
                </div>
              </div>
            ) : (
              <div className="px-6 pt-4 pb-6">
                <TiptapMarkdownEditor
                  content={draftContent}
                  onUpdate={handleEditorChange}
                  markdownEngine="official"
                  placeholder="Type '/' for commands"
                  showToolbar
                />
              </div>
            )}
          </div>
        </div>
      </FullscreenOverlayBase>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onDiscard={handleDialogDiscard}
        onSave={handleDialogSave}
        onCancel={handleDialogCancel}
      />
    </>
  )
}
